import {CleanHtml, EditorEmbeds, EmbedProps, emptyCleanHtml, sanitizeHtml} from '@frontapp/editor-html';
import {compact, debounce, isUndefined, throttle} from 'lodash';
import memoize from 'lru-memoize';
import React, {Component, createRef, RefCallback} from 'react';
import ReactDOMServer from 'react-dom/server';
import styled from 'styled-components';

import {KnowledgeBaseInsertHandler} from '../../../../libs/knowledge-base/shared/components/composerIntegration/knowledgeBaseComposerDropdown';
import {buildPublicArticleUrl} from '../../../../libs/knowledge-base/shared/helpers/urlHelpers';
import {globalDocument} from '../../../../libs/shared/core/src/helpers/browser/browserHelpers';
import {sanitizeHref} from '../../../../libs/shared/core/src/helpers/browser/urlHelpers';
import {buildInteractionIdProps} from '../../../../libs/shared/core/src/helpers/interaction/interactionHelpers';
import {I18n} from '../../../../libs/shared/core/src/helpers/internationalization/i18n';
import {Trans} from '../../../../libs/shared/core/src/helpers/internationalization/react';
import {EmailFormatsEnum} from '../../../../libs/shared/core/src/helpers/models/emailFormatHelpers';
import {DeferredFunc} from '../../../../libs/shared/core/src/helpers/utilities/asyncHelpers';
import {isHtml} from '../../../../libs/shared/core/src/helpers/utilities/stringHelpers';
import {stringSimilarity} from '../../../../libs/shared/core/src/helpers/utilities/stringSimilarity';
import {UploadedAttachmentModel} from '../../../../libs/shared/core/src/models/api/attachmentModel';
import {ChannelModel} from '../../../../libs/shared/core/src/models/api/channelModel';
import {CompanyModel} from '../../../../libs/shared/core/src/models/api/company/companyModel';
import {MessageErrorModel} from '../../../../libs/shared/core/src/models/api/messageErrorModel';
import {DraftMessageModel} from '../../../../libs/shared/core/src/models/api/messageModel';
import {RecipientRolesEnum} from '../../../../libs/shared/core/src/models/api/recipientModel';
import {reportToBugsnag} from '../../../../libs/shared/core/src/services/bugsnag/bugsnag';
import {PushProps, withPush} from '../../../../libs/shared/core/src/services/push/pushContext';
import {
  InterfaceEventsEnum,
  trackInterfaceEvent,
} from '../../../../libs/shared/core/src/services/tracking/tracking';
import {
  ComposerPanelId,
  RViewerPanelId,
  ViewerPanelId,
} from '../../../../libs/shared/core/src/store/panels/panelStateBase';
import {TimelineVariationsEnum} from '../../../../libs/shared/core/src/store/timelineVariations/timelineVariationsTypes';
import {SimpleDropdownItem} from '../../../../libs/shared/ui/src/components/dropdowns/simpleDropdown/simpleDropdownItem';
import {removeDarkModeClassNames} from '../../../../libs/shared/ui/src/styles/darkModeEditorHelpers';
import {
  growToFitHeightContainerStyle,
  growToFitHeightStyle,
} from '../../../../libs/shared/ui/src/styles/styleHelpers';
import {
  ThemeControllerContextProps,
  withThemeController,
} from '../../../../libs/shared/ui/src/theme/themeControllerContext';
import {shortcodeToUnified} from '../../../../srcCommon/helpers/emoji/emojiTextHelpers';
import {inlineAttachmentAllowedAttributes} from '../../../../srcCommon/helpers/messageDraftHelpers';
import {
  PrecogProps,
  PrecogUpdateTypesEnum,
  withMessagePrecog,
} from '../../../../srcCommon/inboxes/composer/precogs/precogHooks';
import {htmlToText} from '../../../../srcCommon/services/converters/htmlToTextConverter';
import {getFileContentType} from '../../../helpers/fileHelpers';
import {getImageSize, getInlineImageAttachmentHtml} from '../../../helpers/imageHelpers';
import {
  buildIntegrationFileAttachmentHtml,
  buildIntegrationFileAttachmentMarkdown,
  IntegrationFileAttachment,
} from '../../../helpers/integrationHelpers';
import {
  findDraftValidationErrors,
  isDraftInNewConversation,
  SendDraftErrorsEnum,
} from '../../../helpers/models/messageHelpers';
import {MessagePatch} from '../../../helpers/models/messagePatchHelpers';
import {AnswerModel} from '../../../models/api/answerModel';
import {CardModel} from '../../../models/api/cardModel';
import {GifModel, GiphyRenditionsEnum} from '../../../models/api/gifModel';
import {SchedulingLinkModel} from '../../../models/api/schedulingLinkModel';
import {SearchTagModel} from '../../../models/api/searchTagModel';
import {SignatureModel} from '../../../models/api/signatureModel';
import {TagModel} from '../../../models/api/tagModel';
import {MessageComposerApi, registerMessageComposer} from '../../../services/composers';
import {htmlToMarkdown} from '../../../services/converters/htmlToMarkdownConverter';
import {markdownToHtml} from '../../../services/converters/markdownToHtmlConverter';
import {EmojiMap} from '../../../services/emojis/emojis';
import {memoizeVariableHtmlInterpolation} from '../../../services/variableInterpolation/memoizeVariableInterpolation';
import {VariableInterpolator} from '../../../services/variableInterpolation/variableInterpolator';
import {DraggedAttachmentItemsModel} from '../../common/attachments/attachmentsDragAndDrop';
import {AttachmentsEditorProps} from '../../common/attachments/attachmentsEditor';
import {AttachmentsPasteTarget} from '../../common/attachments/attachmentsPasteTarget';
import {ChangeManager} from '../../common/editor/change/changeManager';
import {HtmlEditorChangeManager} from '../../common/editor/change/htmlEditorChangeManager';
import {EmojiEditorDropdown} from '../../common/editor/dropdowns/emojiEditorDropdown';
import {QuickInsertContainer} from '../../common/editor/dropdowns/quickInsert/quickInsertContainer';
import {InsertTableDefinition} from '../../common/editor/dropdowns/quickInsert/quickInsertTableDropdown';
import {VariableCompletionDropdown} from '../../common/editor/dropdowns/variableCompletionDropdown';
import {
  ContentEditableHtmlEditor,
  createContentEditableHtmlEditor,
} from '../../common/editor/html/contentEditableHtmlEditor';
import {MarkdownEditor} from '../../common/editor/markdown/markdownEditor';
import {createMaybeIgnorePasteAsTextPreferencePlugin} from '../../common/editor/plugins/html/maybeIgnorePasteAsTextPlugin';
import {
  createSchedulingLinkPlugin,
  findSchedulingLinkIdFromElement,
} from '../../common/editor/plugins/html/schedulingLinkPlugin';
import {
  createInlineImageFromTextPlugin,
  InlineImagesHtmlDefinition,
} from '../../common/editor/plugins/inlineImagesFromTextPlugin';
import {
  addAnswerVariableStyles,
  createAnswerVariablePlugin,
  maybeSelectPillText,
  RawVariable,
  updateAnswerVariables,
} from '../../common/editor/plugins/text/answerVariablePlugin';
import {insertEmojiInEditor} from '../../common/editor/plugins/text/emojiPlugin';
import {createSurveyPlugin} from '../../common/editor/plugins/text/surveyPlugin';
import {ServiceSubscription} from '../../common/editor/serviceSubscription';
import {
  deserializeDefaultFontStyle,
  serializeDefaultFontStyle,
} from '../../common/editor/style/defaultFontStyle';
import {HtmlStyle} from '../../common/editor/style/htmlStyle';
import {ThemeControllerDropdownItem} from '../viewer/activity/message/messageThemeController';
import {ComposerBannerContainer} from './banner/composerBannerContainer';
import {ChannelSaver} from './channelPicker/pickChannelDropdownContainer';
import {EmailComposerHtmlEditor} from './common/emailComposerHtmlEditor';
import {ComposerAttachments} from './composerAttachments';
import {ComposerBase} from './composerBase';
import {FocusModesEnum} from './composerDetails';
import {ComposerResolveConflictsModal} from './composerResolveConflictsModal';
import {EmailComposerHeader, EmailComposerHeaderProps} from './emailComposerHeader';
import {FormattingMarkdownToolbar} from './formatting/formattingMarkdownToolbar';
import {FormattingToolbarContainer} from './formatting/formattingToolbarContainer';
import {ForwardContainer} from './forward/forwardContainer';
import {QuoteContainer} from './quote/quoteContainer';
import {MeetingTimesData} from './scheduling/meetingsettings/meetingSettingsPanel';
import {SchedulingLinkAvailabilityList} from './scheduling/schedulingLinkAvailabilityList';
import {SchedulingLinkAvailabilityListEmbed} from './scheduling/schedulingLinkAvailabilityListEmbed';
import {SignaturesContainer} from './signatures/signaturesContainer';
import {
  replaceSurveyPreviewWithTemplateVariable,
  replaceSurveyTemplateVariableWithPreview,
} from './signatures/survey';
import {SendMessage} from './toolbar/buttons/sendToolbarButton';
import {FullComposerToolbar, FullComposerToolbarProps} from './toolbar/fullComposerToolbar';

/*
 * Constants.
 */

const saveDebounceTime = 1000; // ms
const inlineImageMaxWidth = 600; // px

/** Optimize the editor layout if it's taller than this. */
const optimizeEditorLayoutHeight = 3000; // px

const initialGifHeight = 200; // px
const maxGifHeight = 400; // px

/*
 * Props.
 */

export interface EmailComposerProps
  extends Omit<EmailComposerHeaderProps, 'subject' | 'onSubjectChange' | 'onSubjectBlur' | 'focusMode'>,
    Omit<
      FullComposerToolbarProps,
      'onIntegrationAttachLinks' | 'editorServices' | 'channelId' | 'isMarkdownEditor'
    >,
    AttachmentsEditorProps,
    PrecogProps,
    PushProps,
    ThemeControllerContextProps {
  channel: ChannelModel;
  panelId: ComposerPanelId | ViewerPanelId;
  message: DraftMessageModel;
  company: CompanyModel;
  selectedSignature: SignatureModel | undefined;
  saveError?: MessageErrorModel;
  attemptedSendDraftAttachments: boolean;
  dismissAttemptedSendDraftAttachments: () => void;
  reloadMessage: () => void;
  saveChannel: ChannelSaver;
  saveMessageAsync: (patch: MessagePatch) => void;
  setIsFormattingToolbarOpen: (arg: boolean) => void;
  interpolator: VariableInterpolator;
  addAttachment: (model: UploadedAttachmentModel) => void;
  onDropAttachmentItems: (model: DraggedAttachmentItemsModel) => void;
  updateLastEmailFormat: (arg: EmailFormatsEnum) => void;
  navigateToLinkAction: (arg: string) => void;
  defaultFontStyle: string;
  updateDefaultFontStyle: (arg: string) => void;
  shouldPasteAsText: boolean;
  tagMessage: (model: SearchTagModel) => void;
  untagMessage: (model: TagModel) => void;
  selectedTagIds: ReadonlyArray<number>;
  dontFocusComposer?: boolean;
  shouldDynamicallyGrowComposer?: boolean;
  hasCsatSurveysFeature: boolean;
  i18n: I18n;
  emojiValidationMap: EmojiMap;
  isComposerPopup: boolean;
  setVersion: (arg: string | undefined) => void;
  hasListAutoFormatting: boolean;
  toggleListAutoFormatting: () => void;
  hasRepeatFormatting: boolean;
  onPaste: () => void;
  isComposerPopout: boolean;
  hasRawVariables: boolean;
  onRawVariables: (arg: readonly RawVariable[]) => void;
  onNewRawVariable: (variable: RawVariable) => void;
  hasRtlLanguageSupport: boolean;
  hasAICompose?: boolean;

  fetchAccountsForCardsAsyncAction: (arg: ReadonlyArray<CardModel>) => void;
  cardForInterpolation?: CardModel;
  hasTablePasteIgnorePasteAsText?: boolean;
  hasMarkdownInEmailComposer: boolean;
  viewerConversationCloseRequested: (panelId: ViewerPanelId, conversationId: number) => void;
}

/*
 * Style.
 */

interface WrapperStyleProps {
  $shouldDynamicallyGrowComposer?: boolean;
}
const StyledWrapperDiv = styled.div<WrapperStyleProps>`
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  overflow: ${(p) => (p.$shouldDynamicallyGrowComposer ? 'initial' : 'hidden')};
`;

interface EditorStyleProps {
  $shouldOptimizeEditorLayout?: boolean;
}
const StyledEditorSpacerDiv = styled.div<EditorStyleProps>`
  display: ${(p) => (p.$shouldOptimizeEditorLayout ? 'initial' : 'none')};

  /* Reserve a minimum amount of space for the absolutely positioned contenteditable elements. */
  height: ${optimizeEditorLayoutHeight}px;
`;

const StyledEditorWrapperDiv = styled.div<EditorStyleProps>`
  ${growToFitHeightStyle};
  ${growToFitHeightContainerStyle};

  /* Chrome spends way less time laying out if we absolute position the contenteditable elements. */
  position: ${(p) => (p.$shouldOptimizeEditorLayout ? 'absolute' : 'initial')};
`;

const StyledBodyWrapperDiv = styled.div`
  ${growToFitHeightStyle};
  ${growToFitHeightContainerStyle};

  ${addAnswerVariableStyles()}
`;

const StyledAttachmentsPasteTarget = styled(AttachmentsPasteTarget)`
  ${growToFitHeightStyle};
  ${growToFitHeightContainerStyle};
`;

const StyledBodyHtmlEditor = styled(EmailComposerHtmlEditor)`
  ${growToFitHeightStyle};
  min-height: 90px;
`;

const StyledBodyMarkdownEditor = styled(MarkdownEditor)`
  ${growToFitHeightStyle};
  color: ${(p) => p.theme.greys.black};
  padding: 16px 16px 8px 16px;
`;

const StyledPlaceholderOverlayDiv = styled.div`
  position: absolute;
  pointer-events: none;
  padding: 15px 16px;
  line-height: 20px;
`;

const StyledPlaceholderDiv = styled.div`
  color: ${(p) => p.theme.composerPlaceholder};
`;

/*
 * Component.
 */

interface EmailComposerState {
  subject: string;
  prevActiveEditor: EmailComposerEditorsEnum;
  activeEditor: EmailComposerEditorsEnum | undefined;
  bodyFormat: EmailFormatsEnum;
  bodyMarkdown?: string;
  isQuoteVisible: boolean;
  isForwardVisible: boolean;
  isFormattingToolbarOpen: boolean;
  focusMode?: FocusModesEnum;
  shouldOptimizeEditorLayout: boolean;
  isResolveConflictsModalOpen: boolean;
  cannedAnswerId?: number;
  hasPasteAsTextBeenIgnored?: boolean;
}

enum EmailComposerEditorsEnum {
  BODY = 'BODY',
  SIGNATURE = 'SIGNATURE',
  QUOTE = 'QUOTE',
  FORWARD = 'FORWARD',
}

type EmailComposerEditorDictionary<T> = {[K in EmailComposerEditorsEnum]: T};

type BodyMessagePatch = Required<
  Pick<MessagePatch, 'newFormat' | 'newHtml' | 'newText' | 'newDefaultFontStyle'>
> &
  Pick<MessagePatch, 'newCannedAnswerId'>;

// TODO: Refactor to be format-agnostic, rather than Markdown being a special case.
class EmailComposerInternal
  extends Component<EmailComposerProps, EmailComposerState>
  implements MessageComposerApi
{
  constructor(props: EmailComposerProps) {
    super(props);

    this.state = {
      subject: props.message.subject,
      prevActiveEditor: EmailComposerEditorsEnum.BODY,
      activeEditor: undefined,
      bodyFormat: props.message.format || EmailFormatsEnum.HTML,
      bodyMarkdown: props.message.format === EmailFormatsEnum.MARKDOWN ? props.message.text : undefined,
      isQuoteVisible: Boolean(props.message.wasQuoteModified) || isDraftInNewConversation(props.message),
      isForwardVisible: Boolean(props.message.wasForwardModified) || isDraftInNewConversation(props.message),
      isFormattingToolbarOpen: Boolean(props.isFormattingToolbarOpen),
      isResolveConflictsModalOpen: false,
      focusMode: findInitialFocusMode(props.message, props.dontFocusComposer),
      shouldOptimizeEditorLayout: false,
      cannedAnswerId: props.message.cannedAnswerId,
    };

    const {message, shouldPasteAsText, emojiValidationMap} = this.props;
    this.bodyHtmlEditor = createContentEditableHtmlEditor({
      defaultStyle: this.getHtmlEditorDefaultStyle(),
      shouldPasteAsText,
      plugins: compact([
        createAnswerVariablePlugin({
          getInterpolator: () => this.props.interpolator,
          onRawVariables: props.onRawVariables,
          onNewRawVariable: props.onNewRawVariable,
        }),
        createSchedulingLinkPlugin(this.renderSchedulingLinkEmbed),
        createInlineImageFromTextPlugin(this.onImagesAndHtmlPaste),
        this.props.hasTablePasteIgnorePasteAsText &&
          createMaybeIgnorePasteAsTextPreferencePlugin({
            getShouldPasteAsText: () => this.props.shouldPasteAsText,
            onPasteAsTextIgnored: () => {
              this.setState({
                hasPasteAsTextBeenIgnored: true,
              });
            },
          }),
      ]),
      hasParagraphSpaceIndent: false,
      emojiValidationMap,
      getHasListAutoFormatting: () => this.props.hasListAutoFormatting,
      hasTextDirectionService: props.hasRtlLanguageSupport,
      hasListServiceV2: true,
      hasCodeServices: true,
    });
    this.bodyHtmlEditor.setCleanHtml(message.html);

    this.signatureEditor = createContentEditableHtmlEditor({
      simplifyStyles: true,
      shouldPasteAsText,
      plugins: [createSurveyPlugin(), createInlineImageFromTextPlugin(this.onImagesAndHtmlPaste)],
      hasParagraphSpaceIndent: false,
      emojiValidationMap,
      hasListServiceV2: true,
    });
    this.signatureEditor.setCleanHtml(this.getSignatureHtml() || emptyCleanHtml);

    this.quoteEditor = createContentEditableHtmlEditor({
      shouldPasteAsText,
      hasParagraphSpaceIndent: false,
      emojiValidationMap,
      hasListServiceV2: true,
      plugins: [createInlineImageFromTextPlugin(this.onImagesAndHtmlPaste)],
    });
    this.quoteEditor.setCleanHtml(message.quoteHtml || emptyCleanHtml);

    this.forwardEditor = createContentEditableHtmlEditor({
      shouldPasteAsText,
      hasParagraphSpaceIndent: false,
      emojiValidationMap,
      hasListServiceV2: true,
      plugins: [createInlineImageFromTextPlugin(this.onImagesAndHtmlPaste)],
    });
    this.forwardEditor.setCleanHtml(message.forwardHtml || emptyCleanHtml);

    this.htmlEditors = {
      [EmailComposerEditorsEnum.BODY]: this.bodyHtmlEditor,
      [EmailComposerEditorsEnum.SIGNATURE]: this.signatureEditor,
      [EmailComposerEditorsEnum.QUOTE]: this.quoteEditor,
      [EmailComposerEditorsEnum.FORWARD]: this.forwardEditor,
    };

    this.bodyHtmlChangeManager = new HtmlEditorChangeManager(this.bodyHtmlEditor);
    this.signatureChangeManager = new HtmlEditorChangeManager(this.signatureEditor);
    this.quoteChangeManager = new HtmlEditorChangeManager(this.quoteEditor);
    this.forwardChangeManager = new HtmlEditorChangeManager(this.forwardEditor);
    this.changeManagers = [
      this.bodyHtmlChangeManager,
      this.signatureChangeManager,
      this.quoteChangeManager,
      this.forwardChangeManager,
    ];
  }

  private readonly wrapperRef = createRef<HTMLDivElement>();
  private readonly editorWrapperRef = createRef<HTMLDivElement>();
  private readonly bodyWrapperRef = createRef<HTMLDivElement>();
  private lastBodyHeight: number | undefined;
  private unregisterComposer?: () => void;
  private bodyMarkdownEditorElement: HTMLTextAreaElement | null = null;

  private readonly bodyHtmlEditor: ContentEditableHtmlEditor;
  private readonly signatureEditor: ContentEditableHtmlEditor;
  private readonly quoteEditor: ContentEditableHtmlEditor;
  private readonly forwardEditor: ContentEditableHtmlEditor;
  private readonly htmlEditors: Readonly<EmailComposerEditorDictionary<ContentEditableHtmlEditor>>;

  private getActiveHtmlEditor(
    defaultEditor = EmailComposerEditorsEnum.BODY,
  ): ContentEditableHtmlEditor | undefined {
    const activeEditor = this.state.activeEditor || defaultEditor;
    if (
      activeEditor === EmailComposerEditorsEnum.BODY &&
      this.state.bodyFormat === EmailFormatsEnum.MARKDOWN
    ) {
      return undefined;
    }

    return this.htmlEditors[activeEditor];
  }

  private readonly bodyHtmlChangeManager: ChangeManager;
  private readonly signatureChangeManager: ChangeManager;
  private readonly quoteChangeManager: ChangeManager;
  private readonly forwardChangeManager: ChangeManager;
  private readonly changeManagers: ReadonlyArray<ChangeManager>;

  // For tracking only. Used to track how much an auto-generated draft is edited before sending.
  private draftAssistantMessage: string | null = null;

  private readonly deserializeDefaultFontStyleMemoized = memoize(1)(deserializeDefaultFontStyle);

  private getHtmlEditorDefaultStyle(): HtmlStyle {
    return this.deserializeDefaultFontStyleMemoized(this.props.defaultFontStyle);
  }

  private getSignatureHtml(): CleanHtml | undefined {
    const {message, selectedSignature, interpolator, hasCsatSurveysFeature} = this.props;
    const {signatureHtml, wasSignatureModified} = message;
    const html = wasSignatureModified ? signatureHtml : selectedSignature?.body;

    if (!html) {
      return undefined;
    }

    const htmlWithPreview = replaceSurveyTemplateVariableWithPreview(html, hasCsatSurveysFeature);

    return this.interpolateSignatureHtmlMemoized(htmlWithPreview, interpolator);
  }

  private readonly interpolateSignatureHtmlMemoized = memoizeVariableHtmlInterpolation();

  /** Insert any string into the message body, regardless of whether it's HTML or Markdown. */
  private insertString(value: string, forceInline?: boolean) {
    switch (this.state.bodyFormat) {
      case EmailFormatsEnum.HTML: {
        const editor = this.getActiveHtmlEditor(this.state.prevActiveEditor) || this.bodyHtmlEditor;
        editor.services.edit.insertDirtyHtml(value);
        // TODO: Make the canned answers dropdown restore focus automatically, just like a dialog does?
        editor.services.focus.focus();
        break;
      }
      case EmailFormatsEnum.MARKDOWN:
        this.setState(
          ({bodyMarkdown}) => {
            const markdown = bodyMarkdown || '';
            const delimiter = forceInline || markdown.match(/(^|[\r\n\f]+)\s*$/) ? '' : '\n';
            const text = isHtml(value) ? htmlToText(value) : value;
            return {bodyMarkdown: `${markdown}${delimiter}${text}`, focusMode: FocusModesEnum.BODY};
          },
          () => {
            const element = this.bodyMarkdownEditorElement;
            if (!element) {
              return;
            }

            element.focus();
          },
        );
        break;

      default:
    }
  }

  private setString(value: string) {
    switch (this.state.bodyFormat) {
      case EmailFormatsEnum.HTML: {
        const editor = this.getActiveHtmlEditor(this.state.prevActiveEditor) || this.bodyHtmlEditor;
        editor.services.html.setDirtyHtml(value);
        editor.services.focus.focus();
        break;
      }

      case EmailFormatsEnum.MARKDOWN:
        this.setState(
          () => {
            const bodyMarkdown = isHtml(value) ? htmlToText(value) : value;
            return {bodyMarkdown, focusMode: FocusModesEnum.BODY};
          },
          () => {
            const element = this.bodyMarkdownEditorElement;
            if (!element) {
              return;
            }

            element.focus();
          },
        );
        break;

      default:
    }
  }

  private getBodyWrapperHeight(): number | undefined {
    const wrapper = this.bodyWrapperRef.current;
    if (!wrapper) {
      return undefined;
    }

    const {height} = wrapper.getBoundingClientRect();
    return height;
  }

  /*
   * Editor event handlers.
   */

  private readonly onFileUpload = async (file: File, shouldPreventInline?: boolean) => {
    const isEditingHtml =
      this.state.bodyFormat === EmailFormatsEnum.HTML ||
      !isUndefined(this.getActiveHtmlEditor(this.state.prevActiveEditor));

    // Upload the file inline if an image was pasted or dropped.
    const isInline = !shouldPreventInline && isEditingHtml && getFileContentType(file).startsWith('image/');
    const upload = this.props.onFileUpload(file, {isInline});

    // If not inline, we're done.
    if (!isInline) {
      return;
    }

    const editor = this.getActiveHtmlEditor(this.state.prevActiveEditor) || this.bodyHtmlEditor;

    editor.services.edit.insertDeferredCleanHtml(async () => {
      // Upload the image and determine its size.
      const [{cid, url}, size] = await Promise.all([
        upload.uploadedAttachment,
        getImageSize(file).catch(() => undefined),
      ] as const);

      // Constrain the image size, if necessary.
      const width = size && size.width > inlineImageMaxWidth ? inlineImageMaxWidth : undefined;

      return getInlineImageAttachmentHtml(cid, url, file.name, width);
    });
  };

  private readonly onImagesAndHtmlPaste = async (definition: InlineImagesHtmlDefinition) => {
    const {images} = definition;
    const editor = this.getActiveHtmlEditor(this.state.prevActiveEditor) || this.bodyHtmlEditor;

    editor.services.edit.insertDeferredCleanHtml(async () => {
      for await (const image of images) {
        const {file} = image;
        const upload = this.props.onFileUpload(file, {isInline: true});

        // Upload the image and determine its size.
        const [{cid, url}, size] = await Promise.all([
          upload.uploadedAttachment,
          getImageSize(file).catch(() => undefined),
        ] as const);

        // Constrain the image size, if necessary.
        const width = size && size.width > inlineImageMaxWidth ? inlineImageMaxWidth : undefined;

        const inlineAttachmentHtml = getInlineImageAttachmentHtml(cid, url, file.name, width);
        const imageInDoc = definition.document.getElementsByClassName(image.id)?.item(0);

        if (imageInDoc) {
          imageInDoc.outerHTML = inlineAttachmentHtml;
        }
      }

      return sanitizeHtml(definition.document.documentElement.outerHTML, {
        addAllowedAttributes: inlineAttachmentAllowedAttributes,
      });
    });
  };

  private readonly onEditorFocus: (arg: EmailComposerEditorsEnum) => void = (editor) => {
    this.setState(({prevActiveEditor, activeEditor}) => ({
      prevActiveEditor: activeEditor || prevActiveEditor,
      activeEditor: editor,
    }));
  };

  private readonly onEditorBlur: (arg: EmailComposerEditorsEnum) => void = (editor) => {
    // Flush any pending changes when an editor loses focus.
    this.saveContentDebounced.flush();

    this.setState(({activeEditor}) => {
      if (activeEditor !== editor) {
        return null;
      }

      return {
        prevActiveEditor: activeEditor,
        activeEditor: undefined,
      };
    });
  };

  private readonly onBodyMarkdownChange: (arg: string) => void = (bodyMarkdown) => {
    this.setState({bodyMarkdown});
  };

  private readonly bodyMarkdownEditorElementRefCallback: RefCallback<HTMLTextAreaElement> = (element) => {
    this.bodyMarkdownEditorElement = element;
  };

  private wasContentSaved = false;
  private readonly saveContentDebounced = debounceSave(() => this.saveContent());

  private saveContent() {
    this.wasContentSaved = true;

    // Cancel any pending saves when we are called directly.
    this.saveContentDebounced.cancel();

    // Collect all the text and HTML.
    this.props.saveMessageAsync({
      ...this.buildForwardMessagePatch(),
      ...this.buildQuoteMessagePatch(),
      ...this.buildSignatureMessagePatch(),
      ...this.buildBodyMessagePatch(),
      ...this.buildSubjectMessagePatch(),
    });
  }

  private processHtmlBeforeSave(html: CleanHtml): CleanHtml {
    try {
      // It's not critical to remove these classnames, since they're
      // obfuscated by our backend, but report if errors do happen.
      return removeDarkModeClassNames(html);
    } catch (e) {
      reportToBugsnag(e);
      return html;
    }
  }

  private buildBodyMessagePatch(): BodyMessagePatch {
    const newFormat = this.state.bodyFormat;
    const newDefaultFontStyle = this.props.defaultFontStyle;
    const newCannedAnswerId = this.state.cannedAnswerId;
    const options = {newFormat, newDefaultFontStyle, newCannedAnswerId};

    if (newFormat === EmailFormatsEnum.MARKDOWN) {
      const newText = this.state.bodyMarkdown || '';
      const newHtml = markdownToHtml(newText);
      return {...options, newHtml, newText};
    }

    const newHtml = this.processHtmlBeforeSave(this.bodyHtmlEditor.cleanHtml);

    return {...options, newHtml, newText: ''};
  }

  private buildSignatureMessagePatch(): Partial<MessagePatch> {
    const {message, selectedSignature, hasCsatSurveysFeature} = this.props;
    if (!selectedSignature) {
      return {};
    }

    const isSignatureModified = message.wasSignatureModified || this.signatureChangeManager.isDirty;

    const signatureHtmlCleaned = this.processHtmlBeforeSave(
      // Before saving the html replace the preview of CSAT with the template variable to pass to the backend
      replaceSurveyPreviewWithTemplateVariable(this.signatureEditor.cleanHtml, hasCsatSurveysFeature),
    );

    // Save the signature HTML regardless of whether it was modified, because the editor may have simplified styling
    // and performed other normalizations on it.
    return {newSignatureHtml: signatureHtmlCleaned, newWasSignatureModified: isSignatureModified};
  }

  private buildQuoteMessagePatch(): Partial<MessagePatch> {
    const {message} = this.props;
    if (isUndefined(message.quoteHtml)) {
      return {};
    }

    const isQuoteModified = message.wasQuoteModified || this.quoteChangeManager.isDirty;
    if (!isQuoteModified) {
      return {};
    }

    const newQuoteHtml = this.processHtmlBeforeSave(this.quoteEditor.cleanHtml);

    return {newQuoteHtml, newWasQuoteModified: true};
  }

  private buildForwardMessagePatch(): Partial<MessagePatch> {
    const {message} = this.props;
    if (isUndefined(message.forwardHtml)) {
      return {};
    }

    const isForwardModified = message.wasForwardModified || this.forwardChangeManager.isDirty;
    if (!isForwardModified) {
      return {};
    }

    const newForwardHtml = this.processHtmlBeforeSave(this.forwardEditor.cleanHtml);

    return {newForwardHtml, newWasForwardModified: true};
  }

  private buildSubjectMessagePatch(): Partial<MessagePatch> {
    const {subject} = this.state;
    const {interpolator} = this.props;
    return {newSubject: interpolator.interpolateText(subject)};
  }

  private readonly onBodyFormatChange: (arg: EmailFormatsEnum) => void = (newFormat) => {
    switch (newFormat) {
      case EmailFormatsEnum.HTML:
        this.convertBodyToHtml();
        break;

      case EmailFormatsEnum.MARKDOWN:
        this.convertBodyToMarkdown();
        break;

      default:
    }
  };

  private convertBodyToHtml() {
    this.setState(
      ({bodyFormat, bodyMarkdown}) => {
        // Only convert Markdown to HTML.
        if (bodyFormat !== EmailFormatsEnum.MARKDOWN) {
          return null;
        }

        // Set the new HTML on the editor, which causes the HTML to be normalized, and prepares the editor for when the
        // body format state change is applied.
        const html = markdownToHtml(bodyMarkdown || '');
        this.bodyHtmlChangeManager.markAsPristine(() => {
          this.bodyHtmlEditor.setCleanHtml(html);
        });

        // Set the new body format in our state.
        return {bodyFormat: EmailFormatsEnum.HTML};
      },
      () => {
        // Trigger a message save once all the sources of truth have been updated.
        this.saveContent();
      },
    );
  }

  private convertBodyToMarkdown() {
    this.setState(
      ({bodyFormat}) => {
        // Only convert HTML to Markdown.
        if (bodyFormat !== EmailFormatsEnum.HTML) {
          return null;
        }

        const {html} = this.bodyHtmlEditor;
        const bodyMarkdown = htmlToMarkdown(html);

        return {bodyFormat: EmailFormatsEnum.MARKDOWN, bodyMarkdown};
      },
      () => {
        // Trigger a message save once all the sources of truth have been updated.
        this.saveContent();
      },
    );
  }

  private readonly onSaveDefaultStyleClick: () => void = () => {
    const {style} = this.bodyHtmlEditor.services.htmlStyle;
    const newDefaultFontStyle = serializeDefaultFontStyle(style);
    this.props.updateDefaultFontStyle(newDefaultFontStyle);
  };

  private readonly onSubjectChange: (arg: string) => void = (subject) => {
    this.setState({subject});
  };

  private readonly onSubjectBlur: () => void = () => {
    // Flush any pending changes when an editor loses focus.
    this.saveContentDebounced.flush();
  };

  private readonly onEmojiSelect: (arg: string) => void = (shortcode) => {
    if (this.state.bodyFormat === EmailFormatsEnum.MARKDOWN) {
      const unifiedEmoji = shortcodeToUnified(shortcode);
      this.insertString(unifiedEmoji, true);
      return;
    }

    const editor = this.getActiveHtmlEditor(this.state.prevActiveEditor);
    if (!editor) {
      return;
    }

    insertEmojiInEditor(editor, shortcode);
  };

  private readonly onGifSelect: (model: GifModel) => void = (gif) => {
    const editor = this.getActiveHtmlEditor(this.state.prevActiveEditor);
    if (!editor) {
      return;
    }

    editor.services.image.insertImageWithUrl(gif.images[GiphyRenditionsEnum.DOWNSIZED].url, {
      initialHeight: initialGifHeight,
      maxHeight: maxGifHeight,
    });
    editor.services.focus.focus();
  };

  private readonly onToggleIsQuoteVisible: () => void = () => {
    this.quoteChangeManager.ignoreChanges((callback) => {
      this.setState(({isQuoteVisible}) => ({isQuoteVisible: !isQuoteVisible}), callback);
    });
  };

  private readonly onToggleIsForwardVisible: () => void = () => {
    this.forwardChangeManager.ignoreChanges((callback) => {
      this.setState(({isForwardVisible}) => ({isForwardVisible: !isForwardVisible}), callback);
    });
  };

  /** Flush this draft and any unsaved changes to the server. */
  private flushUnsaved() {
    // If the body/signature/quote haven't been changed through the UI yet, go ahead and save any non-UI
    // changes now (such as signature HTML/style normalization).
    if (!this.wasContentSaved) {
      this.saveContentDebounced();
    }

    this.saveContentDebounced.flush();
  }

  private discardUnsaved() {
    this.wasContentSaved = true;

    this.saveContentDebounced.cancel();
  }

  /*
   * Toolbar event handlers.
   */

  // If we inserted a generated draft, compute how much it was edited before it was sent.
  private maybeTrackDraftAssistantSendSimilarity() {
    if (!this.draftAssistantMessage) {
      return;
    }

    const bodyText =
      this.state.bodyFormat === EmailFormatsEnum.HTML
        ? this.bodyHtmlEditor.services.text.text
        : this.state.bodyMarkdown;

    if (!bodyText) {
      return;
    }

    const similarity = stringSimilarity(this.draftAssistantMessage, bodyText);

    if (similarity === null) {
      return;
    }

    trackInterfaceEvent(InterfaceEventsEnum.AI_COMPOSE_SEND_SIMILARITY, {
      message_uid: this.props.message.uid,
      similarity,
    });
  }

  private readonly sendMessage: SendMessage = (options) => {
    this.flushUnsaved();

    this.props.sendMessage(options);

    this.maybeTrackDraftAssistantSendSimilarity();

    this.props.updateLastEmailFormat(this.state.bodyFormat);
  };

  private readonly deleteMessage = () => {
    this.discardUnsaved();

    this.props.deleteMessage();
    // Close the window if we're in a popout if there are no  other messages in the convo
    if (RViewerPanelId.is(this.props.panelId) && this.props.conversation) {
      this.props.viewerConversationCloseRequested(this.props.panelId, this.props.conversation.id);
    }
  };

  private readonly toggleIsFormattingToolbarOpen = () => {
    // Compute the new value.
    const {isFormattingToolbarOpen} = this.state;
    const newIsFormattingToolbarOpen = !isFormattingToolbarOpen;

    // Update the local state, and the local preference.
    this.setState({isFormattingToolbarOpen: newIsFormattingToolbarOpen});
    this.props.setIsFormattingToolbarOpen(newIsFormattingToolbarOpen);
  };

  private readonly setCannedAnswerId = (cannedAnswerId: number) => {
    this.setState({cannedAnswerId});
  };

  private readonly onAnswerSelect: (model: AnswerModel) => void = (answer) => {
    const {interpolator} = this.props;

    if (answer.attachments.length) {
      answer.attachments.forEach((attachment) => {
        this.props.addAttachment(attachment);
      });
    }

    const answerSubject = answer.subject;
    if (answerSubject) {
      this.onSubjectChange(answerSubject);
      this.onSubjectBlur();
    }

    // For HTML bodies, let the plugin add answer pills and update the values dynamically.
    // For Markdown bodies, just replace the text once.
    const answerBody =
      this.state.bodyFormat === EmailFormatsEnum.HTML
        ? answer.body
        : interpolator.interpolateHtml(answer.body);

    this.insertString(answerBody);

    // The canned answer id is needed for analytics, but the message we send is formatted on the front end.
    // We do not store this canned answer id to the store, it is only used for this editor session.
    this.setCannedAnswerId(answer.id);
  };

  private readonly onSchedulingLinkInsertMeetingTimes: (data: MeetingTimesData) => void = (
    meetingTimesData,
  ) => {
    this.bodyHtmlEditor.services.embed.insertEmbed(
      <div data-front-scheduling-link-id={meetingTimesData.linkId} style={{position: 'relative'}}>
        {this.renderSchedulingLinkAvailability(meetingTimesData)}
      </div>,
    );
  };

  private readonly onSchedulingLinkInsertLink: (model: SchedulingLinkModel) => void = (schedulingLink) => {
    const anchor = globalDocument.createElement('a');
    anchor.href = sanitizeHref(schedulingLink.bookingPageUrl);
    anchor.target = '_blank';
    anchor.textContent = schedulingLink.bookingPageUrl;
    this.insertHtml(anchor.outerHTML);
  };

  private readonly onKnowledgeBaseInsertContent: KnowledgeBaseInsertHandler = ({
    article,
    revision,
    category,
    locale,
    knowledgeBase,
    headerText,
  }) => {
    const url = article
      ? buildPublicArticleUrl(knowledgeBase, article.id, locale, headerText)
      : category?.url;
    if (!url) {
      return;
    }

    const name = (() => {
      if (revision) {
        return revision.subject;
      }
      if (locale && category) {
        return category.names[locale];
      }
      return url;
    })();

    const anchor = globalDocument.createElement('a');
    anchor.href = sanitizeHref(url);
    anchor.target = '_blank';
    anchor.textContent = name;
    if (headerText && article) {
      anchor.textContent = `${name}: ${headerText}`;
    }
    this.insertHtml(anchor.outerHTML);
  };

  private readonly onTableInsertContent: (definition: InsertTableDefinition) => void = (tableDef) => {
    this.insertHtml(generateTableForDefinition(tableDef));
  };

  private readonly renderSchedulingLinkAvailability = (meetingTimesData: MeetingTimesData) => (
    <SchedulingLinkAvailabilityList meetingTimesData={meetingTimesData} i18n={this.props.i18n} />
  );

  private readonly renderSchedulingLinkEmbed = (domNode: Element, embedProps: EmbedProps) => {
    const linkId = findSchedulingLinkIdFromElement(domNode);
    if (!linkId) {
      return null;
    }

    return (
      <SchedulingLinkAvailabilityListEmbed
        onUpdateContent={embedProps.onUpdateContent}
        onDeleteEmbed={embedProps.onDeleteEmbed}
        parentElement={domNode}
        schedulingLinkId={linkId}
        renderAvailability={this.renderSchedulingLinkAvailability}
        panelId={this.props.panelId}
      />
    );
  };

  private readonly onDraftAssistantInsertDraft: (arg: string) => void = (draftMessage) => {
    this.draftAssistantMessage = htmlToText(draftMessage);
    this.setHtml(draftMessage);
  };

  private readonly onIntegrationAttachLink: (arg: ReadonlyArray<IntegrationFileAttachment>) => void = (
    attachments,
  ) => {
    const html = attachments
      .map((a) => {
        if (this.state.bodyFormat === EmailFormatsEnum.HTML) {
          return buildIntegrationFileAttachmentHtml(a);
        }

        return buildIntegrationFileAttachmentMarkdown(a);
      })
      .join('');
    this.insertString(html);
  };

  private readonly updateShouldOptimizeEditorLayout = throttle(
    () => {
      const composerWrapper = this.wrapperRef.current;
      const editorWrapper = this.editorWrapperRef.current;
      if (!composerWrapper || !editorWrapper) {
        return;
      }

      const {height: composerHeight} = composerWrapper.getBoundingClientRect();
      const {height: editorHeight} = editorWrapper.getBoundingClientRect();
      const isScrollingInsideComposer = editorHeight > composerHeight;
      const newShouldOptimizeEditorLayout =
        isScrollingInsideComposer && editorHeight >= optimizeEditorLayoutHeight;

      this.setState(({shouldOptimizeEditorLayout}) => {
        if (shouldOptimizeEditorLayout === newShouldOptimizeEditorLayout) {
          return null;
        }

        return {shouldOptimizeEditorLayout: newShouldOptimizeEditorLayout};
      });
    },
    1000,
    {leading: true, trailing: true},
  );

  /*
   * Lifecycle.
   */

  componentDidMount() {
    this.changeManagers.forEach((changeManager) => {
      changeManager.subscribe((isDirty) => {
        this.updateShouldOptimizeEditorLayout();

        // Avoid saving a previously unsaved draft when a non-UI change was made.
        if (!this.wasContentSaved && !isDirty) {
          return;
        }

        this.saveContentDebounced();
        this.props.updatePrecog(PrecogUpdateTypesEnum.CONTINUE);
      });
    });

    this.registerComposer();

    this.updateVariables();
  }

  getSnapshotBeforeUpdate() {
    // We need this during rendering.
    this.lastBodyHeight = this.getBodyWrapperHeight();

    // We don't need anything in componentDidUpdate.
    return null;
  }

  componentDidUpdate(
    previousProps: Readonly<EmailComposerProps>,
    previousState: Readonly<EmailComposerState>,
  ) {
    this.maybeUpdateDefaultFontStyle(previousProps.defaultFontStyle);
    this.maybeSaveBodyMarkdown(previousState.bodyMarkdown);
    this.maybeSaveSubject(previousProps, previousState);
    this.maybeResetSignatureHtml(previousProps);
    this.maybeResetQuoteHtml(previousProps);
    this.maybeResetForwardHtml(previousProps);
    this.maybeUpdateVariables(previousProps);
    this.maybeFetchAccountForInterpolation(previousProps);

    if (this.props.message.uid !== previousProps.message.uid) {
      throw new Error('EmailComposer must be keyed by message uid');
    }

    this.updateShouldOptimizeEditorLayout();
  }

  componentWillUnmount() {
    this.changeManagers.forEach((changeManager) => changeManager.unsubscribe());

    // Assume editor blur has flushed changes, but cancel debounce timers just in case.
    this.discardUnsaved();

    if (this.unregisterComposer) {
      this.unregisterComposer();
    }
  }

  private maybeUpdateDefaultFontStyle(previousDefaultFontStyle: string | undefined) {
    if (this.props.defaultFontStyle === previousDefaultFontStyle) {
      return;
    }

    this.bodyHtmlEditor.services.htmlStyle.setDefaultStyle(this.getHtmlEditorDefaultStyle());
  }

  private maybeSaveBodyMarkdown(previousBodyMarkdown: string | undefined) {
    const {bodyMarkdown} = this.state;
    if (bodyMarkdown === previousBodyMarkdown) {
      return;
    }

    this.saveContentDebounced();
  }

  private maybeSaveSubject(
    previousProps: Readonly<EmailComposerProps>,
    previousState: Readonly<EmailComposerState>,
  ) {
    const {subject} = this.state;
    const {interpolator} = this.props;
    const variablesEqual = interpolator.isEqual(previousProps.interpolator);

    // TODO: Avoid saving the message content when the subject has no variables, which is most of the time.
    if (subject === previousState.subject && variablesEqual) {
      return;
    }

    this.saveContentDebounced();
  }

  // maybeResetSignatureHtml must use setDirtyHtml because the content might not be sanitary after variable
  // interpolation, but maybeResetQuoteHtml and maybeResetForwardHtml can use setCleanHtml since the content
  // is the raw version provided from the server.
  private maybeResetSignatureHtml(prevProps: EmailComposerProps) {
    if (!signatureNeedsReset(this.props, prevProps)) {
      return;
    }

    this.signatureChangeManager.markAsPristine(() => {
      this.signatureEditor.setDirtyHtml(this.getSignatureHtml() || '');
    });
  }

  private maybeResetQuoteHtml(prevProps: EmailComposerProps) {
    const quoteWasReverted = prevProps.message.wasQuoteModified && !this.props.message.wasQuoteModified;
    if (!quoteWasReverted) {
      return;
    }

    this.quoteChangeManager.markAsPristine(() => {
      this.quoteEditor.setCleanHtml(this.props.message.quoteHtml || emptyCleanHtml);
    });
  }

  private maybeResetForwardHtml(prevProps: EmailComposerProps) {
    const forwardWasReverted = prevProps.message.wasForwardModified && !this.props.message.wasForwardModified;
    if (!forwardWasReverted) {
      return;
    }

    this.forwardChangeManager.markAsPristine(() => {
      this.forwardEditor.setCleanHtml(this.props.message.forwardHtml || emptyCleanHtml);
    });
  }

  private maybeUpdateVariables(prevProps: EmailComposerProps) {
    const {interpolator} = this.props;

    const variablesChanged = !interpolator.isEqual(prevProps.interpolator);

    if (variablesChanged) {
      this.updateVariables();
    }
  }

  private updateVariables() {
    if (this.bodyWrapperRef.current) {
      const rawVariables = updateAnswerVariables(this.bodyWrapperRef.current, this.props.interpolator);
      this.props.onRawVariables(rawVariables);
    }
  }

  private maybeFetchAccountForInterpolation(prevProps: EmailComposerProps) {
    if (
      !this.props.cardForInterpolation ||
      prevProps.cardForInterpolation?.accountId === this.props.cardForInterpolation?.accountId
    ) {
      return;
    }

    this.props.fetchAccountsForCardsAsyncAction([this.props.cardForInterpolation]);
  }

  private registerComposer() {
    if (this.unregisterComposer) {
      this.unregisterComposer();
    }

    this.unregisterComposer = registerMessageComposer(this.props.message.uid, this);
  }

  private onResolveMessageConflicts = () => {
    this.setState({isResolveConflictsModalOpen: true});
  };

  private onResolveLatest = () => {
    this.setState({isResolveConflictsModalOpen: false});
    this.props.reloadMessage();
  };

  private onDiscardLatest = (latestVersion?: string) => {
    this.setState({isResolveConflictsModalOpen: false});
    this.props.setVersion(latestVersion);
    this.saveContent();
  };

  /*
   * API.
   */

  focus() {
    this.bodyHtmlEditor.focus();
  }

  insertHtml(html: string) {
    this.insertString(html);
  }

  insertSubject(value: string) {
    this.setState(({subject}) => ({subject: `${subject}${value}`}));
  }

  saveMessage = (messagePatch: MessagePatch) => {
    this.props.saveMessageAsync(messagePatch);
  };

  setHtml(html: string) {
    this.setString(html);
  }

  setSubject(value: string) {
    this.setState({subject: value});
  }

  /*
   * Render.
   */

  render() {
    const {message, channel, attachments, onDropAttachmentItems, shouldDynamicallyGrowComposer, company} =
      this.props;
    const validationErrors = findDraftValidationErrors(message, channel, company, {attachments});
    return (
      <StyledWrapperDiv
        ref={this.wrapperRef}
        $shouldDynamicallyGrowComposer={shouldDynamicallyGrowComposer}
        {...buildInteractionIdProps('email-composer')}
      >
        <ComposerBase
          header={this.renderHeader()}
          editor={this.renderEditor()}
          toolbar={(isFocused) => this.renderToolbar(validationErrors, isFocused)}
          onDropFile={this.onFileUpload}
          onDropAttachmentItems={onDropAttachmentItems}
          shouldDynamicallyGrowComposer={shouldDynamicallyGrowComposer}
          isComposerPopout={this.props.isComposerPopout}
          shouldRenderInlineGrammarlyButton={this.state.bodyFormat === EmailFormatsEnum.HTML}
        />
        <EditorEmbeds embedService={this.bodyHtmlEditor.services.embed} />
        {this.maybeRenderResolveConflictsModal()}
      </StyledWrapperDiv>
    );
  }

  private renderHeader() {
    return (
      <EmailComposerHeader
        recipients={this.props.recipients}
        conversation={this.props.conversation}
        user={this.props.user}
        onPopout={this.props.onPopout}
        isComposerPopup={this.props.isComposerPopup}
        channel={this.props.channel}
        message={this.props.message}
        namespace={this.props.namespace}
        addRecipients={this.props.addRecipients}
        removeRecipients={this.props.removeRecipients}
        saveChannel={this.props.saveChannel}
        className={this.props.className}
        subject={this.props.interpolator.interpolateText(this.state.subject)}
        onSubjectChange={this.onSubjectChange}
        onSubjectBlur={this.onSubjectBlur}
        focusMode={this.state.focusMode}
        panelId={this.props.panelId}
      />
    );
  }

  private renderEditor() {
    const {shouldOptimizeEditorLayout} = this.state;
    return (
      <>
        <StyledEditorSpacerDiv $shouldOptimizeEditorLayout={shouldOptimizeEditorLayout} />
        <StyledEditorWrapperDiv
          ref={this.editorWrapperRef}
          $shouldOptimizeEditorLayout={shouldOptimizeEditorLayout}
        >
          <ComposerAttachments
            attachments={this.props.attachments}
            onRemoveAttachmentClick={this.props.onRemoveAttachmentClick}
            onCancelAttachmentUploadClick={this.props.onCancelAttachmentUploadClick}
          />
          <StyledBodyWrapperDiv
            ref={this.bodyWrapperRef}
            onClick={(evt) => maybeSelectPillText(evt.target, evt.currentTarget)}
          >
            {this.renderBodyEditor()}
          </StyledBodyWrapperDiv>
          {this.renderNonBodyEditors()}
          {this.maybeRenderEmojiDropdown()}
          {this.maybeRenderVariableCompletionDropdown()}
          {this.maybeRenderQuickInsertDropdown()}
        </StyledEditorWrapperDiv>
      </>
    );
  }

  private renderToolbar(validationErrors: ReadonlyArray<SendDraftErrorsEnum>, isFocused: boolean) {
    const {
      message,
      channel,
      reloadMessage,
      saveError,
      attemptedSendDraftAttachments,
      dismissAttemptedSendDraftAttachments,
      navigateToLinkAction,
      conflictLink,
    } = this.props;
    const {isFormattingToolbarOpen, hasPasteAsTextBeenIgnored} = this.state;
    const hasValidationErrors = validationErrors.length > 0 || Boolean(message.error);

    return (
      <>
        <ComposerBannerContainer
          hasPasteAsTextBeenIgnored={hasPasteAsTextBeenIgnored}
          dismissIgnoredPasteAsText={() => {
            this.setState({hasPasteAsTextBeenIgnored: false});
          }}
          validationErrors={validationErrors}
          conversation={this.props.conversation}
          message={message}
          channel={channel}
          hasRawVariables={this.props.hasRawVariables}
          saveMessage={() => this.saveMessage({})}
          reloadMessage={reloadMessage}
          onResolveMessageConflicts={this.onResolveMessageConflicts}
          saveError={saveError}
          attemptedSendDraftAttachments={attemptedSendDraftAttachments}
          dismissAttemptedSendDraftAttachments={dismissAttemptedSendDraftAttachments}
          conflictLinkAction={conflictLink ? () => navigateToLinkAction(conflictLink) : undefined}
          isPopup={this.props.isComposerPopup}
        />
        {this.maybeRenderFormattingToolbar()}
        <FullComposerToolbar
          isUserVisitor={this.props.isUserVisitor}
          conversation={this.props.conversation}
          draftSaveStatus={this.props.draftSaveStatus}
          isDraftSaving={this.props.isDraftSaving}
          channel={channel}
          message={message}
          namespace={this.props.namespace}
          interpolator={this.props.interpolator}
          cellsContext={this.props.cellsContext}
          hasClosePermission={this.props.hasClosePermission}
          hasSendPermission={this.props.hasSendPermission}
          defaultSendAction={this.props.defaultSendAction}
          hasTrialExpired={this.props.hasTrialExpired}
          hasTeamTrialExpired={this.props.hasTeamTrialExpired}
          doneWithMessage={this.props.doneWithMessage}
          createTag={this.props.createTag}
          teams={this.props.teams}
          userTeams={this.props.userTeams}
          fetchTagsAsync={this.props.fetchTagsAsync}
          tagMessage={this.props.tagMessage}
          untagMessage={this.props.untagMessage}
          selectedTagIds={this.props.selectedTagIds}
          panelId={this.props.panelId}
          onEmojiSelect={this.onEmojiSelect}
          onGifSelect={this.onGifSelect}
          onIntegrationAttachLinks={this.onIntegrationAttachLink}
          onFileUpload={this.props.onFileUpload}
          onAnswerSelect={this.onAnswerSelect}
          onSchedulingLinkInsertMeetingTimes={this.onSchedulingLinkInsertMeetingTimes}
          onSchedulingLinkInsertLink={this.onSchedulingLinkInsertLink}
          onKnowledgeBaseInsertContent={this.onKnowledgeBaseInsertContent}
          onDraftAssistantInsertDraft={this.onDraftAssistantInsertDraft}
          composerEntryPoints={this.props.composerEntryPoints}
          supportsFormatting
          supportsGifs
          supportsScheduling
          hasValidationErrors={hasValidationErrors}
          areSendShortcutsDisabled={!isFocused}
          sendMessage={this.sendMessage}
          deleteMessage={this.deleteMessage}
          isFormattingToolbarOpen={isFormattingToolbarOpen}
          toggleIsFormattingToolbarOpen={this.toggleIsFormattingToolbarOpen}
          editorServices={this.bodyHtmlEditor.services}
          timelineVariation={TimelineVariationsEnum.ASYNCHRONOUS}
          hasAICompose={this.props.hasAICompose}
          isAIDisabled={this.props.isAIDisabled}
          isMarkdownEditor={this.state.bodyFormat === EmailFormatsEnum.MARKDOWN}
          hasSaveErrorMessage={Boolean(saveError?.description)}
        />
      </>
    );
  }

  private renderNonBodyEditors() {
    return (
      <AttachmentsPasteTarget onPasteFile={this.onFileUpload}>
        {this.renderSignatureEditor()}
        {this.renderQuoteEditor()}
        {this.renderForwardEditor()}
      </AttachmentsPasteTarget>
    );
  }

  private renderBodyEditor() {
    switch (this.state.bodyFormat) {
      case EmailFormatsEnum.HTML:
        return this.renderBodyHtmlEditor();

      case EmailFormatsEnum.MARKDOWN:
        return this.renderBodyMarkdownEditor();

      default:
        return null;
    }
  }

  private renderBodyHtmlEditor() {
    const editor = this.bodyHtmlEditor;

    return (
      <StyledAttachmentsPasteTarget onPasteFile={this.onFileUpload}>
        <ServiceSubscription source={editor.services.empty}>
          {({isEmpty}) => isEmpty && renderPlaceholderOverlay()}
        </ServiceSubscription>
        <StyledBodyHtmlEditor
          editor={editor}
          isFirstEditor
          defaultStyle={this.props.defaultFontStyle}
          shouldFocus={this.state.focusMode === FocusModesEnum.BODY}
          onFocus={() => this.onEditorFocus(EmailComposerEditorsEnum.BODY)}
          onBlur={() => this.onEditorBlur(EmailComposerEditorsEnum.BODY)}
          onPaste={this.props.onPaste}
        />
      </StyledAttachmentsPasteTarget>
    );
  }

  private renderBodyMarkdownEditor() {
    const {bodyMarkdown, focusMode} = this.state;

    return (
      <StyledBodyMarkdownEditor
        value={bodyMarkdown || ''}
        onChange={this.onBodyMarkdownChange}
        textAreaRef={this.bodyMarkdownEditorElementRefCallback}
        shouldFocus={focusMode === FocusModesEnum.BODY}
        // Reduce visual jitter when switching from HTML to Markdown.
        initialHeight={this.lastBodyHeight}
        onFocus={() => this.onEditorFocus(EmailComposerEditorsEnum.BODY)}
        onBlur={() => this.onEditorBlur(EmailComposerEditorsEnum.BODY)}
        onPaste={this.props.onPaste}
      />
    );
  }

  private renderSignatureEditor() {
    const editor = this.signatureEditor;
    const {activeEditor} = this.state;

    return (
      <SignaturesContainer
        channel={this.props.channel}
        message={this.props.message}
        recipients={this.props.recipients}
        saveMessageAsync={this.props.saveMessageAsync}
        signatureHtml={this.getSignatureHtml()}
        isAlwaysEditing
        renderEditor={() => (
          <EmailComposerHtmlEditor
            editor={editor}
            onFocus={() => this.onEditorFocus(EmailComposerEditorsEnum.SIGNATURE)}
            onBlur={() => this.onEditorBlur(EmailComposerEditorsEnum.SIGNATURE)}
          />
        )}
        hasFocus={activeEditor === EmailComposerEditorsEnum.SIGNATURE}
      />
    );
  }

  private renderQuoteEditor() {
    const editor = this.quoteEditor;
    const {activeEditor, isQuoteVisible} = this.state;

    return (
      <QuoteContainer
        message={this.props.message}
        saveMessageAsync={this.props.saveMessageAsync}
        isQuoteVisible={isQuoteVisible}
        onToggleIsQuoteVisible={this.onToggleIsQuoteVisible}
        isAlwaysEditing
        renderEditor={() => (
          <EmailComposerHtmlEditor
            editor={editor}
            shouldAddQuoteStyle
            onFocus={() => this.onEditorFocus(EmailComposerEditorsEnum.QUOTE)}
            onBlur={() => this.onEditorBlur(EmailComposerEditorsEnum.QUOTE)}
          />
        )}
        hasFocus={activeEditor === EmailComposerEditorsEnum.QUOTE}
      />
    );
  }

  private renderForwardEditor() {
    const editor = this.forwardEditor;
    const {activeEditor, isForwardVisible} = this.state;

    return (
      <ForwardContainer
        message={this.props.message}
        saveMessageAsync={this.props.saveMessageAsync}
        isForwardVisible={isForwardVisible}
        onToggleIsForwardVisible={this.onToggleIsForwardVisible}
        isAlwaysEditing
        renderEditor={() => (
          <EmailComposerHtmlEditor
            editor={editor}
            onFocus={() => this.onEditorFocus(EmailComposerEditorsEnum.FORWARD)}
            onBlur={() => this.onEditorBlur(EmailComposerEditorsEnum.FORWARD)}
          />
        )}
        hasFocus={activeEditor === EmailComposerEditorsEnum.FORWARD}
      />
    );
  }

  private maybeRenderEmojiDropdown() {
    const editor = this.getActiveHtmlEditor();
    if (!editor) {
      return null;
    }

    return <EmojiEditorDropdown autocompleteService={editor.services.autocomplete} />;
  }

  private maybeRenderQuickInsertDropdown() {
    const editor = this.getActiveHtmlEditor();

    if (!editor) {
      return null;
    }

    return (
      <QuickInsertContainer
        panelId={this.props.panelId}
        channelId={this.props.channel.id}
        conversation={this.props.conversation}
        editorServices={editor.services}
        editorWrapperRef={this.editorWrapperRef}
        interpolator={this.props.interpolator}
        message={this.props.message}
        namespace={this.props.namespace}
        companyUrl={this.props.company.url}
        onAnswerSelect={this.onAnswerSelect}
        onSchedulingLinkInsertLink={this.onSchedulingLinkInsertLink}
        onKnowledgeBaseInsertContent={this.onKnowledgeBaseInsertContent}
        onTableInsert={this.onTableInsertContent}
      />
    );
  }

  private maybeRenderVariableCompletionDropdown() {
    const editor = this.getActiveHtmlEditor();
    if (!editor) {
      return null;
    }

    return <VariableCompletionDropdown autocompleteService={editor.services.autocomplete} />;
  }

  private maybeRenderFormattingToolbar() {
    const activeEditor = this.state.activeEditor || EmailComposerEditorsEnum.BODY;

    if (
      activeEditor === EmailComposerEditorsEnum.BODY &&
      this.state.bodyFormat === EmailFormatsEnum.MARKDOWN
    ) {
      return this.renderBodyMarkdownFormattingToolbar();
    }

    return this.renderHtmlEditorFormattingToolbar(this.htmlEditors[activeEditor]);
  }

  private renderBodyMarkdownFormattingToolbar() {
    return (
      <FormattingMarkdownToolbar
        isVisible={this.state.isFormattingToolbarOpen}
        renderMoreDropdownItems={() => this.renderMoreFormattingToolbarDropdownItems()}
      />
    );
  }

  private renderHtmlEditorFormattingToolbar(htmlEditor: ContentEditableHtmlEditor) {
    const {isFormattingToolbarOpen} = this.state;
    const {hasListAutoFormatting, toggleListAutoFormatting, hasRepeatFormatting} = this.props;

    return (
      <ServiceSubscription source={htmlEditor.services.focus}>
        {({isActive}) => (
          <FormattingToolbarContainer
            isVisible={isFormattingToolbarOpen}
            htmlEditor={htmlEditor}
            onSaveDefaultStyleClick={this.onSaveDefaultStyleClick}
            onImageUpload={this.onFileUpload}
            isKeyboardShortcutsEnabled={isActive}
            renderMoreDropdownItems={() => this.renderMoreFormattingToolbarDropdownItems()}
            hasListAutoFormatting={hasListAutoFormatting}
            toggleListAutoFormatting={toggleListAutoFormatting}
            hasRepeatFormatting={hasRepeatFormatting}
          />
        )}
      </ServiceSubscription>
    );
  }

  private renderMoreFormattingToolbarDropdownItems() {
    return (
      <>
        {this.maybeRenderMarkdownDropdownItem()}
        <ThemeControllerDropdownItem
          shouldRenderThemeOverrideUi={this.props.shouldRenderThemeOverrideUi}
          isLightThemeOverride={this.props.isLightThemeOverride}
          onToggleLightThemeOverride={this.props.onToggleLightThemeOverride}
        />
      </>
    );
  }

  private maybeRenderMarkdownDropdownItem() {
    const {activeEditor} = this.state;
    const {hasMarkdownInEmailComposer} = this.props;
    if (
      (!isUndefined(activeEditor) && activeEditor !== EmailComposerEditorsEnum.BODY) ||
      !hasMarkdownInEmailComposer
    ) {
      return null;
    }

    const isMarkdownActive = this.state.bodyFormat === EmailFormatsEnum.MARKDOWN;
    const onMarkdownSwitchClick = () =>
      this.onBodyFormatChange(isMarkdownActive ? EmailFormatsEnum.HTML : EmailFormatsEnum.MARKDOWN);

    return (
      <SimpleDropdownItem onSelect={onMarkdownSwitchClick}>
        {renderMarkdownDropdownLabel(isMarkdownActive)}
      </SimpleDropdownItem>
    );
  }

  private maybeRenderResolveConflictsModal() {
    if (!this.state.isResolveConflictsModalOpen) {
      return null;
    }

    return (
      <ComposerResolveConflictsModal
        message={this.props.message}
        panelId={this.props.panelId}
        onModalClose={() => this.setState({isResolveConflictsModalOpen: false})}
        onResolveLatest={this.onResolveLatest}
        onDiscardLatest={this.onDiscardLatest}
      />
    );
  }
}

function renderPlaceholderOverlay() {
  return (
    <StyledPlaceholderOverlayDiv>
      <StyledPlaceholderDiv>
        <Trans id="composer-emailplaceholder-quick-insert">Type / to insert a message template</Trans>
      </StyledPlaceholderDiv>
    </StyledPlaceholderOverlayDiv>
  );
}

/*
 * Helpers.
 */

function renderMarkdownDropdownLabel(isMarkdownActive: boolean) {
  if (isMarkdownActive) {
    return <Trans id="formatting-richtext">Switch to rich text</Trans>;
  }

  return <Trans id="formatting-plaintext">Switch to Markdown</Trans>;
}

function signatureWasReverted(props: EmailComposerProps, prevProps: EmailComposerProps): boolean {
  return !!prevProps.message.wasSignatureModified && !props.message.wasSignatureModified;
}

function signatureWasReplaced(props: EmailComposerProps, prevProps: EmailComposerProps): boolean {
  return prevProps.message.signatureId !== props.message.signatureId;
}

function signatureNeedsReinterpolation(props: EmailComposerProps, prevProps: EmailComposerProps): boolean {
  return !prevProps.message.wasSignatureModified && !prevProps.interpolator.isEqual(props.interpolator);
}

function signatureNeedsReset(props: EmailComposerProps, prevProps: EmailComposerProps): boolean {
  return (
    signatureWasReverted(props, prevProps) ||
    signatureWasReplaced(props, prevProps) ||
    signatureNeedsReinterpolation(props, prevProps)
  );
}

function findInitialFocusMode(message: DraftMessageModel, dontFocusComposer?: boolean) {
  if (dontFocusComposer) {
    return undefined;
  }

  const toRecipients = message.recipients.filter((r) => r.role === RecipientRolesEnum.TO);
  if (toRecipients.length === 0) {
    return FocusModesEnum.TO;
  }

  if (!message.subject || !message.subject.trim()) {
    return FocusModesEnum.SUBJECT;
  }

  return FocusModesEnum.BODY;
}

function debounceSave<T extends (this: unknown, ...args: any[]) => void>(saveFunc: T): DeferredFunc<T> {
  let isFlushing = false;
  let lastInvocationError: any;

  const debouncedFunc = debounce((...args: Parameters<T>): void => {
    try {
      saveFunc(...args);
      lastInvocationError = undefined;
    } catch (e) {
      if (!isFlushing) {
        // eslint-disable-next-line no-console
        console.error(e);
      }

      lastInvocationError = e;
    }
  }, saveDebounceTime);

  const originalFlush = debouncedFunc.flush.bind(debouncedFunc);
  debouncedFunc.flush = () => {
    isFlushing = true;
    originalFlush();
    isFlushing = false;

    if (lastInvocationError) {
      throw lastInvocationError;
    }

    return undefined;
  };

  return debouncedFunc;
}

function generateTableForDefinition(tableDefinition: InsertTableDefinition) {
  return ReactDOMServer.renderToStaticMarkup(
    <table
      style={{
        borderCollapse: 'collapse',
        border: `1px solid black`,
      }}
    >
      <colgroup>
        {Array.from({length: tableDefinition.cols}).map((_col, columnIndex) => (
          // eslint-disable-next-line react/no-array-index-key
          <col width="100" key={`colgroup-${columnIndex}`} />
        ))}
      </colgroup>
      <tbody>
        {Array.from({length: tableDefinition.rows}).map((_row, rowIndex) => (
          // eslint-disable-next-line react/no-array-index-key
          <tr key={rowIndex} style={{borderCollapse: 'collapse', border: `1px solid black`}}>
            {Array.from({length: tableDefinition.cols}).map((_col, columnIndex) => (
              // eslint-disable-next-line jsx-a11y/control-has-associated-label
              <td
                // eslint-disable-next-line react/no-array-index-key
                key={`${columnIndex}-${rowIndex}`}
                style={{borderCollapse: 'collapse', border: `1px solid black`}}
              >
                <br />
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>,
  );
}

export const EmailComposer = withPush(withMessagePrecog(withThemeController(EmailComposerInternal)));
